import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DailyCallStats } from './types';
import { getCrmDailyStats, getErrorMessage } from './adminApi';

// Distinct, accessible palette assigned to agents by stack order (busiest first).
const AGENT_COLORS = [
  '#2563eb', // blue-600
  '#16a34a', // green-600
  '#f59e0b', // amber-500
  '#db2777', // pink-600
  '#7c3aed', // violet-600
  '#0891b2', // cyan-600
  '#dc2626', // red-600
  '#65a30d', // lime-600
  '#9333ea', // purple-600
  '#0d9488', // teal-600
];

interface TooltipEntry {
  name?: string | number;
  value?: number | string;
  color?: string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
}

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  const entries = payload
    .map((p) => ({ name: String(p.name ?? ''), value: Number(p.value) || 0, color: p.color }))
    .filter((p) => p.value > 0)
    .sort((a, b) => b.value - a.value);
  const total = payload.reduce((sum, p) => sum + (Number(p.value) || 0), 0);

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="mb-1.5 font-semibold text-gray-900">{label}</div>
      {entries.length === 0 ? (
        <div className="text-gray-400">Aucun appel</div>
      ) : (
        <div className="space-y-1">
          {entries.map((p) => (
            <div key={p.name} className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: p.color }} />
              <span className="text-gray-600">{p.name}</span>
              <span className="ml-auto pl-4 font-medium tabular-nums text-gray-900">{p.value}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-1.5 flex items-center gap-2 border-t border-gray-100 pt-1.5">
        <span className="font-medium text-gray-500">Total</span>
        <span className="ml-auto font-semibold tabular-nums text-gray-900">{total}</span>
      </div>
    </div>
  );
}

export default function CallsPerDayChart() {
  const [data, setData] = useState<DailyCallStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getCrmDailyStats()
      .then((d) => { if (mounted) setData(d); })
      .catch((err) => { if (mounted) setError(getErrorMessage(err)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const hasData = data && data.agents.length > 0 && data.days.some((d) => d.total > 0);

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-950">Appels par jour</h3>
        <p className="text-sm text-gray-500">14 derniers jours · réparti par agent</p>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : loading ? (
        <div className="flex h-[300px] items-center justify-center text-sm text-gray-500">Chargement…</div>
      ) : !hasData ? (
        <div className="flex h-[300px] items-center justify-center text-sm text-gray-500">
          Aucun appel sur les 14 derniers jours.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data!.days} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f1f4" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              interval="preserveStartEnd"
              minTickGap={4}
            />
            <YAxis
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              width={36}
            />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
              formatter={(value) => <span className="text-gray-600">{value}</span>}
            />
            {data!.agents.map((agent, i) => (
              <Bar
                key={agent.callerId}
                dataKey={agent.callerId}
                name={agent.label}
                stackId="calls"
                fill={AGENT_COLORS[i % AGENT_COLORS.length]}
                radius={i === data!.agents.length - 1 ? [3, 3, 0, 0] : undefined}
                maxBarSize={44}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
